<template>
  <MainLayout>
    <section id="hero" class="max-w-[1080px] lg:m-[auto] backgroundgradient backgroundgradient-top">
      <div class="grid max-md:grid-rows-1 lg:grid-cols-5 gap-10 lg:mt-14">
        <div class="flex flex-col gap-y-8 col-span-3">
          <h1
            class="text-4xl lg:text-6xl font-bold text-dark font-markPro lg:max-w-[] tracking-[-0.1rem]">
            {{ t('pages.products.pos.hero.title.pt1') }}
            <span class="text-gradient-primary whitespace-nowrap">{{
              t('pages.products.pos.hero.title.pt2')
            }}</span>
            {{ t('pages.products.pos.hero.title.pt3') }}
          </h1>

          <p class="text-lighter lg:max-w-[80%] font-semibold text-lg">
            {{ t('pages.products.pos.hero.description') }}
          </p>

          <div class="flex gap-x-3 gap-y-4 items-center flex-row">
            <a
              v-track-event="'onboarding_start'"
              href="https://merchant.bistroo.nl/register"
              class="btn btn-lg btn-primary whitespace-nowrap">
              {{ t('main.actions.create_your_shop') }}
            </a>
            <a
              href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
              target="_blank"
              class="btn btn-lg btn-dark whitespace-nowrap"
              >{{ t('main.actions.product_demo') }}
            </a>
          </div>
        </div>

        <img
          class="col-span-3 lg:col-span-2 justify-self-center lg:-mt-3"
          src="/images/img-hero-pos.webp"
          alt="Bistroo POS system" />
      </div>

      <img
        class="max-w-[94%] object-bottom opacity-60 lg:pr-10"
        src="/images/img-logos-greyscale.webp"
        alt="Trusted by our partners" />
    </section>

    <section id="paragraphs" class="max-w-[1080px] max-md:-mt-10 lg:m-[auto] lg:!gap-30">
      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-5">
        <div class="col-span-3 flex flex-col gap-y-9 order-1 lg:pl-[5rem] justify-center">
          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[13ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.products.pos.paragraphs.paragraph_1.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[90%] font-semibold">
            {{ t('pages.products.pos.paragraphs.paragraph_1.text') }}
          </p>

          <RouterLink
            :to="{ name: 'pricing.index' }"
            class="max-lg:order-1 text-gradient-primary text-lg font-semibold flex gap-x-2 items-center">
            <span>{{ t('pages.products.pos.paragraphs.paragraph_1.cta') }}</span>
            <i class="bi bi-arrow-right"></i>
          </RouterLink>
        </div>

        <div class="relative lg:col-span-2">
          <img src="/images/img-home-paragraphs-manager.webp" alt="Sandwich store background" />
          <img
            class="absolute top-22 lg:top-16 -right-8 w-[95%]"
            src="/images/img-pos-screen.webp"
            alt="POS ordering screen" />
        </div>
      </div>

      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-7">
        <div class="max-lg:order-2 col-span-4 flex flex-col gap-y-8 lg:pr-20 justify-center">
          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[20ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.products.pos.paragraphs.paragraph_2.title') }}
          </h3>

          <p class="text-lighter font-semibold lg:pr-14">
            {{ t('pages.products.pos.paragraphs.paragraph_2.text') }}
          </p>
        </div>

        <div class="relative col-span-3">
          <img
            class="lg:w-[95%]"
            src="/images/img-pos-sandwichstore.webp"
            alt="Bistroo POS in sandwich store merchant" />
          <img
            class="absolute top-8 -left-9 lg:-left-22 w-60 lg:w-68"
            src="/images/img-pos-realtimeorders.webp"
            alt="Realtime orders toast" />
        </div>
      </div>
    </section>

    <section id="key-features">
      <div class="grid lg:grid-cols-3 gap-8">
        <div class="md:col-span-2 flex flex-col gap-y-8 rounded-lg bg-white drop-shadow">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="text-xl font-semibold text-gradient-primary font-markPro">
                {{ t('pages.products.pos.key_features.cards.card_1.title') }}
              </h4>

              <p class="text-lighter">
                {{ t('pages.products.pos.key_features.cards.card_1.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end max-lg:pt-5"
              src="/images/products/img-posdevice-block-1.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-lg bg-white drop-shadow">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="text-xl font-semibold font-markPro">
                {{ t('pages.products.pos.key_features.cards.card_2.title') }}
              </h4>

              <p class="text-lighter">
                {{ t('pages.products.pos.key_features.cards.card_2.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end pr-12"
              src="/images/products/img-posdevice-block-2.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-lg bg-white drop-shadow">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="text-xl font-semibold text-gradient-primary font-markPro">
                {{ t('pages.products.pos.key_features.cards.card_3.title') }}
              </h4>

              <p class="text-lighter">
                {{ t('pages.products.pos.key_features.cards.card_3.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end px-10 mb-1"
              src="/images/products/img-posdevice-block-3.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-lg bg-white drop-shadow">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="text-xl font-semibold text-gradient-primary font-markPro">
                {{ t('pages.products.pos.key_features.cards.card_4.title') }}
              </h4>

              <p class="text-lighter">
                {{ t('pages.products.pos.key_features.cards.card_4.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end px-10"
              src="/images/products/img-posdevice-block-4.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-8 rounded-lg bg-white drop-shadow">
          <div class="flex flex-col justify-between h-[100%]">
            <div class="flex flex-col gap-y-4 p-8">
              <h4 class="text-xl font-semibold text-gradient-primary font-markPro">
                {{ t('pages.products.pos.key_features.cards.card_5.title') }}
              </h4>

              <p class="text-lighter">
                {{ t('pages.products.pos.key_features.cards.card_5.text') }}
              </p>
            </div>

            <img
              class="rounded-lg overflow-hidden place-self-end"
              src="/images/products/img-posdevice-block-5.webp"
              alt="" />
          </div>
        </div>
      </div>
    </section>

    <section id="paragraphs_2" class="max-w-[1080px] max-md:-mt-10 lg:m-[auto]">
      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-6">
        <div class="max-lg:order-2 col-span-4 flex flex-col gap-y-8 lg:pr-20 pt-14">
          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[20ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.products.pos.paragraphs.paragraph_3.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[75%] font-semibold">
            {{ t('pages.products.pos.paragraphs.paragraph_3.text') }}
          </p>
        </div>

        <div class="relative col-span-2">
          <img src="/images/img-pos-kitchenprinter.webp" alt="Bistroo POS cloud kitchen printer" />
          <img
            class="absolute top-0 lg:-top-5 -left-9 lg:-left-20 w-55"
            src="/images/img-pos-cloudprint.webp"
            alt="Realtime cloud printing technology" />
        </div>
      </div>

      <div
        class="flex flex-col gap-10 max-lg:items-center max-md:flex-col-reverse max-lg:p-4 lg:grid lg:grid-cols-5 lg:pt-10">
        <div class="col-span-3 flex flex-col gap-y-8 order-1 lg:ml-[4rem] lg:mt-16">
          <h3
            class="text-3xl lg:text-5xl font-bold text-dark font-markPro lg:max-w-[20ch] lg:leading-14 tracking-[-0.1rem]">
            {{ t('pages.products.pos.paragraphs.paragraph_4.title') }}
          </h3>

          <p class="text-lighter lg:max-w-[90%] font-semibold">
            {{ t('pages.products.pos.paragraphs.paragraph_4.text') }}
          </p>

          <RouterLink
            :to="{ name: 'pricing.index' }"
            class="max-lg:order-1 text-gradient-primary text-lg font-semibold flex gap-x-2 items-center">
            <span>{{ t('pages.products.pos.paragraphs.paragraph_3.cta') }}</span>
            <i class="bi bi-arrow-right"></i>
          </RouterLink>
        </div>

        <div class="relative lg:col-span-2">
          <img class="w-[85%]" src="/images/img-pos-manager-bg.webp" alt="Sandwich store" />
          <img
            class="absolute top-18.5 left-4 lg:left-10"
            src="/images/img-home-paragraphs-float-manager.webp"
            alt="Bistroo Manager Dashboard" />
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />

            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>

    <section id="faq">
      <div class="w-full rounded-2xl bg-[#F7F9FC] border border-[#E8EDF4] overflow-hidden mb-16">
        <div class="px-8 py-12 lg:px-20 lg:py-16 flex flex-col gap-y-10">

          <div class="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-y-4 gap-x-12 max-w-[800px] mx-auto w-full">
            <div class="flex flex-col gap-y-3">
              <span class="inline-flex items-center gap-x-2 self-start text-sm font-semibold px-4 py-1.5 rounded-full border border-primary/25 bg-[#FFF5FB] text-lighter">
                <span class="w-2 h-2 rounded-full bg-gradient-to-r from-[#FF3696] to-[#FF7C53] inline-block flex-shrink-0"></span>
                {{ t('main.product_faq.label') }}
              </span>
              <h2 class="text-[28px] leading-[34px] lg:text-[40px] lg:leading-[46px] font-bold font-markPro text-dark tracking-[-0.07rem]">
                {{ t('pages.products.pos.faq.title_pt1') }}<br class="hidden lg:block" />
                <span class="text-gradient-primary">{{ t('pages.products.pos.faq.title_pt2') }}</span>
              </h2>
            </div>
            <p class="text-lighter font-semibold leading-relaxed lg:max-w-[38ch] lg:text-right lg:pb-1">
              {{ t('main.product_faq.not_listed') }}
            </p>
          </div>

          <FaqAccordion :items="faqs" />

        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import FaqAccordion from '@/components/FaqAccordion.vue'
import { useHead } from '@unhead/vue'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'

const { t, tm } = useI18n()
const faqs = computed(() => tm('pages.products.pos.faq.items') as { q: string; a: string }[])

useHead(computed(() => ({
  title: t('pages.products.pos.seo.title'),
  meta: [{ name: 'description', content: t('pages.products.pos.seo.description') }],
  script: [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.value.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }),
    },
  ],
})))
</script>

<style scoped></style>
